import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneField } from '@/components/booking/phone-field';
import { COUNTRIES, labelOf, splitPhone, guessCountryIso } from '@/lib/countries';

// Renders one custom field from the Form Builder schema. Pulled out of the
// old ContactForm so question-steps can reuse the same UI (radio/checkbox/
// select/textarea/text-input) without duplicating ~80 lines of markup.
export function CustomFieldInput({ field, value, otherTexts, onChange, onOtherChange }) {
  const setVal = (v) => onChange(field.id, v);

  if (field.type === 'textarea') {
    return (
      <Field field={field}>
        <textarea
          id={field.id}
          value={value || ''}
          onChange={(e) => setVal(e.target.value)}
          placeholder={field.placeholder}
          required={field.required}
          rows={3}
          className="flex w-full rounded-md border border-input bg-field text-field-foreground px-3 py-2 text-sm shadow-sm placeholder:text-field-foreground/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        />
      </Field>
    );
  }

  // Every country, so the tenant never maintains the list. The value stored is
  // the country NAME - what a rep wants to read in the CRM - while the phone
  // field works off the dial code behind it.
  if (field.type === 'country') {
    return (
      <Field field={field}>
        <select
          id={field.id}
          value={value || ''}
          onChange={(e) => setVal(e.target.value)}
          required={field.required}
          className="flex h-10 w-full rounded-md border border-input bg-field text-field-foreground px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        >
          <option value="">{field.placeholder || 'Select your country'}</option>
          {COUNTRIES.map((c) => (
            <option key={c.iso} value={c.name}>{labelOf(c)}</option>
          ))}
        </select>
      </Field>
    );
  }

  // An extra phone question gets the same country-code treatment as the built-in
  // one, so a second number is stored in the same E.164 shape.
  if (field.type === 'tel') {
    const iso = splitPhone(value, guessCountryIso()).iso;
    return (
      <Field field={field}>
        <PhoneField
          id={field.id}
          value={value}
          iso={iso}
          onChange={(e164) => setVal(e164)}
          placeholder={field.placeholder}
          required={field.required}
        />
      </Field>
    );
  }

  if (field.type === 'select') {
    return (
      <Field field={field}>
        <select
          id={field.id}
          value={value || ''}
          onChange={(e) => setVal(e.target.value)}
          required={field.required}
          className="flex h-10 w-full rounded-md border border-input bg-field text-field-foreground px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        >
          <option value="">Select {field.name}</option>
          {field.options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </Field>
    );
  }

  if (field.type === 'radio') {
    return (
      <Field field={field}>
        <div role="radiogroup" className="space-y-1.5">
          {field.options?.map((opt) => {
            const checked = value === opt.value;
            return (
              <label
                key={opt.value}
                htmlFor={`${field.id}_${opt.value}`}
                className={`flex cursor-pointer items-center gap-2.5 rounded-md border bg-field text-field-foreground px-3 py-2 text-sm transition-colors ${
                  checked
                    ? 'border-primary bg-primary-soft/70'
                    : 'border-input hover:bg-muted/50'
                }`}
              >
                <input
                  id={`${field.id}_${opt.value}`}
                  type="radio"
                  name={field.id}
                  value={opt.value}
                  checked={checked}
                  onChange={(e) => setVal(e.target.value)}
                  required={field.required}
                  className="size-4 shrink-0 accent-primary"
                />
                <span className="leading-snug">{opt.label}</span>
              </label>
            );
          })}
        </div>
      </Field>
    );
  }

  if (field.type === 'checkbox') {
    const arr = Array.isArray(value) ? value : [];
    return (
      <Field field={field}>
        <div className="space-y-1.5">
          {field.options?.map((opt) => {
            const checked = arr.includes(opt.value);
            const otherKey = `${field.id}__${opt.value}_text`;
            const otherVal = otherTexts?.[otherKey] || '';
            return (
              <div
                key={opt.value}
                className={`flex items-center gap-2.5 rounded-md border bg-field text-field-foreground px-3 py-2 text-sm transition-colors ${
                  checked
                    ? 'border-primary bg-primary-soft/70'
                    : 'border-input hover:bg-muted/50'
                }`}
              >
                {/* Checkbox is a direct sibling of the label/input so the
                    "Other" text field sits flush next to the box instead
                    of leaving an empty flex-1 column behind a tiny checkbox. */}
                <input
                  id={`${field.id}_${opt.value}`}
                  type="checkbox"
                  value={opt.value}
                  checked={checked}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...arr, opt.value]
                      : arr.filter((v) => v !== opt.value);
                    setVal(next);
                  }}
                  className="size-4 shrink-0 accent-primary"
                />
                {opt.with_input ? (
                  <input
                    type="text"
                    placeholder={opt.label}
                    value={otherVal}
                    onChange={(e) => onOtherChange(otherKey, e.target.value)}
                    onFocus={() => {
                      if (!checked) setVal([...arr, opt.value]);
                    }}
                    className="flex-1 rounded-md border border-input bg-field text-field-foreground px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                ) : (
                  <label
                    htmlFor={`${field.id}_${opt.value}`}
                    className="flex-1 cursor-pointer leading-snug"
                  >
                    {opt.label}
                  </label>
                )}
              </div>
            );
          })}
        </div>
      </Field>
    );
  }

  // text / email / url / number.
  // For url fields we render type=text and rely on a lenient normalize on
  // blur, because the browser's native type=url rejects schemeless input
  // like "linkedin.com/foo" which people type all the time.
  const isUrlField = field.type === 'url';
  return (
    <Field field={field}>
      <Input
        id={field.id}
        type={isUrlField ? 'text' : field.type}
        inputMode={isUrlField ? 'url' : undefined}
        value={value || ''}
        onChange={(e) => setVal(e.target.value)}
        onBlur={isUrlField ? (e) => {
          const v = e.target.value.trim();
          // Looks like a domain (has a dot + tld) but missing scheme: add https://
          if (v && !/^https?:\/\//i.test(v) && /\.[a-z]{2,}/i.test(v)) {
            setVal(`https://${v}`);
          }
        } : undefined}
        placeholder={field.placeholder}
        required={field.required}
      />
    </Field>
  );
}

// Each question lives in its own pastel-tinted card so the label + options
// read as one visual group. Keeps the eye moving down the form quickly.
function Field({ field, children }) {
  return (
    <div className="mm-question rounded-lg border border-primary/25 bg-primary-soft/40 p-3 space-y-2">
      <Label htmlFor={field.id} className="text-sm font-medium leading-snug">
        {field.name} {field.required && <span className="text-destructive">*</span>}
      </Label>
      {children}
    </div>
  );
}
